// hooks/useSegmentSettings.js
import { useDispatch, useSelector } from "react-redux";
import { updateSegmentSetting, showPickedSegmentIcon } from "../redux/reducers/segmentBuilder";
import { withHistory } from "../redux/actions/history";
import {
  getSelectedSegment,
  getWheelColorPalette,
} from "../redux/selectors/segmentBuilderSelectors";
import { solidColors } from "../constants/segmentTools";
import { SEGMENT_COMPUTED_LABELS_ENABLED, SEGMENT_ADAPTIVE_ICONS_ENABLED } from "../constants/featureFlags";
import { isUnnamedSegmentTitle } from "../utils/segmentLabels";
import { usePaletteColor } from "./usePaletteColor";
import { getActiveSubscriptionData } from "../redux/selectors/subscriptionSelectors";
import {
  getOpenWheelTeamPlanTier,
  raiseToTeamPlanTier,
} from "../utils/teamPlanTier";
import { resolveBgWallpaperMode } from "../utils/segmentWallpaper";
// Part B (flag REACT_APP_SEGMENT_PALETTE_COLOR): an explicit Segment Color pick
// marks the segment as a MANUAL override so it is NOT overwritten by the
// palette-derived default. Flag OFF -> the marker is never written.
import { SEGMENT_PALETTE_COLOR_ENABLED } from "../components/SegmentBuilder/Shared/SegmentStyling/logic/segmentPaletteColorFlag";
import { generateWheelPayload } from "./logic/generateWheelPayload";
import { seedPatternColorWallpaper } from "./logic/patternColorSeed";
import {
  pickPaletteColorForNewSegment,
  pickRandomColorForNewSegment,
} from "./logic/newSegmentColor";

export default function useSegmentSettings() {
  const selectedArc = useSelector(getSelectedSegment);
  // T22.120 (D20): on a team wheel the team leader's plan tier counts.
  const { data: currentPlan } = raiseToTeamPlanTier(
    useSelector(getActiveSubscriptionData),
    useSelector(getOpenWheelTeamPlanTier)
  );
  const wheelColorPalette = useSelector(getWheelColorPalette) || {};
  const { getPaletteColor } = usePaletteColor();

  const dispatch = useDispatch();
  const handleChange = (type, sideOrValue, value) => {
    if (selectedArc && selectedArc.settings) {
      const updatedSettings = { ...selectedArc.settings };

      // SEGMENT WALLPAPER (stgfix-1007-P0-segment-fill-bleeds-into-wallpaper):
      // every case below drives ONLY the <Wallpaper> accordion (the open-segment
      // CONTENT-AREA background) — never Segment Fill (the wedge + behind-title
      // surface, written separately by SegmentFillUnified/legacyFillFields.js).
      // Historically both controls wrote the SAME top-level currentBg/bgWallpaper/
      // segmentBgColor/isPattern/pattern*/wallpaperOverlayColor fields, so picking
      // a Segment Fill silently repainted the open panel's background too (the
      // panel reader — SegmentView/pages/Segment — had no way to tell which
      // control last wrote them). Nesting the Wallpaper accordion's OWN writes
      // under `settings.wallpaper` gives it independent storage: Segment Fill
      // never reads or writes this key, so a Fill pick can no longer touch it,
      // and an old segment with a Fill but no Wallpaper simply has no
      // `wallpaper` key (read-side default = plain), never the Fill's value.
      const updatedWallpaper = () => ({ ...(updatedSettings.wallpaper || {}) });

      switch (type) {
        case "segmentBgColor":
          updatedSettings.wallpaper = {
            ...updatedWallpaper(),
            segmentBgColor: getPaletteColor(value),
            currentBg: 0,
          };
          break;
        case "segmentBgGradientColor":
          // daniyal-4847: the "No background" clear tile (and the Gradient tab's
          // clear swatch, which shares this same case) only ever touched
          // segmentBgColor/currentBg — the OLD image URL stayed in bgWallpaper
          // and survived the save, so a deleted photo resurrected on reload/
          // reopen. Clearing bgWallpaper alongside stays correct under the new
          // nested storage.
          updatedSettings.wallpaper = {
            ...updatedWallpaper(),
            segmentBgColor: value,
            currentBg: 3,
            bgWallpaper: "",
          };
          break;
        case "wallpaperTransparency":
          updatedSettings.wallpaper = {
            ...updatedWallpaper(),
            wallpaperTransparency: value,
          };
          break;
        case "wallpaperResizing":
          updatedSettings.wallpaper = {
            ...updatedWallpaper(),
            wallpaperResizing: value,
          };
          break;
        case "addWallpaperOverlayColor":
          updatedSettings.wallpaper = {
            ...updatedWallpaper(),
            wallpaperOverlayColor: getPaletteColor(value),
            currentBg: 1,
          };
          break;
        case "resetWallpaperOverlayColor":
          updatedSettings.wallpaper = {
            ...updatedWallpaper(),
            wallpaperOverlayColor: "ffffff",
          };
          break;
        case "addBgWallpaper":
          // F6.8: removing the image (empty value) must revert to none/solid
          // (0), not leave image-mode (2) with an empty url().
          updatedSettings.wallpaper = {
            ...updatedWallpaper(),
            bgWallpaper: getPaletteColor(value),
            isPattern: sideOrValue,
            currentBg: resolveBgWallpaperMode(value, sideOrValue),
          };
          break;
        case "addPatternId":
          updatedSettings.wallpaper = {
            ...updatedWallpaper(),
            patternId: value,
            isPattern: 1,
            currentBg: 1,
          };
          break;
        case "segmentTitle":
          // DL-029: never persist a computed "Segment N" placeholder (or a blank)
          // as a real name — only actually-typed text is stored; a
          // placeholder-shaped or empty submission reverts the segment to
          // unnamed. Flag OFF keeps the legacy `value || ""` (byte-identical).
          updatedSettings.title =
            SEGMENT_COMPUTED_LABELS_ENABLED && isUnnamedSegmentTitle(value)
              ? ""
              : value || "";
          break;
        case "segmentColor":
          updatedSettings.color = getPaletteColor(value);
          // An explicit user pick pins the Segment Color (Part B): it wins over
          // the palette-derived default so a manual override is preserved.
          if (SEGMENT_PALETTE_COLOR_ENABLED) {
            updatedSettings.segmentColorManual = true;
          }
          break;
        case "segmentIcon":
          updatedSettings.icon = value;
          // Picking a real icon implies it should be shown: clear any prior
          // hidden flag so the pick is not silently swallowed by a stale toggle.
          // An explicit `false`, not `undefined`: JSON.stringify drops an
          // undefined key, so the PUT never told the server to un-hide and a
          // stored icon_hidden=true came back on reload (T22.90).
          if (value) updatedSettings.iconHidden = false;
          if (SEGMENT_ADAPTIVE_ICONS_ENABLED && value) {
            dispatch(withHistory(showPickedSegmentIcon()));
          }
          break;
        // Segment-icon-picker (2026-08-18): per-segment show/hide toggle. Stored
        // on segment.settings.iconHidden (serializes with settings, like
        // iconColor). true = hide the glyph on the segment WITHOUT clearing the
        // stored settings.icon name, so toggling back on restores the same icon
        // with no re-pick. The wheel render sites (ViewWheel + SegmentBuilder)
        // withhold d.data.icon when this is true.
        // T22.90 (MEASURED web-stg main.249d3fd7.js): switching back ON must
        // send icon_hidden=false -- `undefined` was dropped from the PUT body,
        // so the server kept the hidden flag and the icon vanished on reload.
        case "segmentIconHidden":
          updatedSettings.iconHidden = !!value;
          break;
        // Additive: per-segment user-customizable icon color. Stored on the
        // segment settings (spread onto chartData as d.data.iconColor) and read
        // by the wheel renderer's resolveSegmentIconColor, which falls back to
        // the theme-responsive default when unset. Pass null/"" to clear the
        // override and return to the responsive default. No UI wired yet (the
        // picker is Fable's surface) — this is the write-side plumbing.
        case "segmentIconColor":
          updatedSettings.iconColor = value || undefined;
          break;

        case "addPatternColor":
          updatedSettings.wallpaper = {
            ...updatedWallpaper(),
            patternColor: getPaletteColor(value),
            currentBg: 1,
          };
          break;
        case "addPatternColorInitially": {
          // T10.4 (stgfix-10111425): this is <Wallpaper>'s MOUNT-TIME seed, not a
          // user edit — it fires on a bare segment SELECT whenever the segment's
          // wallpaper has no patternColor. It used to write
          // `{patternColor, currentBg: 0}` unconditionally, so selecting a
          // never-touched segment (stored `wallpaper: {}`) changed the save
          // payload -> AutosaveChip "Unsaved changes" -> autosave PUT, with no
          // edit (measured web-stg main.0636c6a5, /builder/507662). See
          // logic/patternColorSeed for when a seed is actually needed.
          const seeded = seedPatternColorWallpaper(
            updatedWallpaper(),
            getPaletteColor(value),
          );
          if (!seeded) return;
          updatedSettings.wallpaper = seeded;
          break;
        }
        case "patternColorType":
          updatedSettings.wallpaper = {
            ...updatedWallpaper(),
            selectedColorType: value ?? 0,
            currentBg: 1,
          };
          break;
        case "addResizeMode":
          updatedSettings.wallpaper = {
            ...updatedWallpaper(),
            resizeMode: value,
          };
          break;
        case "wallpaperHuePercentage":
          updatedSettings.wallpaper = {
            ...updatedWallpaper(),
            wallpaperHuePercentage: value,
          };
          break;
        case "wallpaperHueColor":
          updatedSettings.wallpaper = {
            ...updatedWallpaper(),
            wallpaperHueColor: value,
          };
          break;

        default:
          break;
      }

      dispatch(
        withHistory(
          updateSegmentSetting({
            id: selectedArc.id,
            data: updatedSettings,
          })
        )
      );
    }
  };

  const lightenColor = (color, percent) => {
    const num = parseInt(color.replace("#", ""), 16),
      amt = Math.round(2.55 * percent),
      R = (num >> 16) + amt,
      G = ((num >> 8) & 0x00ff) + amt,
      B = (num & 0x0000ff) + amt;
    return `#${(
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
      .toUpperCase()}`;
  };

  const getColor = (wheelData, index, palette) => {
    const excludedColors = [
      "#FFFFFF",
      "#E9ECEF",
      "#DEE2E6",
      "#CED4DA",
      "#ADB5BD",
      "#868E96",
      "#495057",
      "#FFC9C9",
      "#EEBEFA",
      "#D0BFFF",
      "#BAC8FF",
      "#A5D8FF",
      "#96F2D7",
      "#B2F2BB",
      "#D8F5A2",
      "#FFEC99",
      "#FFD8A8",
      "#343A40",
      "#212529",
    ];
    const arrayLength = wheelData?.length;
    const selectedWheelPalette = palette || wheelColorPalette;
    // T22.359: index === wheelData.length means a Segment is being APPENDED
    // ("+ Segment" and friends) -- give it a colour not already on the wheel.
    // An in-range index is the palette re-colour (useSegmentPalettes
    // updateSegmentColors), which must stay a straight by-position mapping.
    const isNewSegment = index >= (arrayLength || 0);

    if (selectedWheelPalette?.colors?.length && currentPlan?.sub_tier !== 0) {
      const segmentIndex =
        index >= selectedWheelPalette.colors.length
          ? index % selectedWheelPalette.colors.length
          : index;

      const color = isNewSegment
        ? pickPaletteColorForNewSegment(
            wheelData,
            index,
            selectedWheelPalette.colors.map((c) => c?.hex)
          )
        : selectedWheelPalette.colors[segmentIndex]?.hex;
      const colorInner = color ? lightenColor(color, 20) : null;

      return { color, colorInner };
    } else {
      const color = pickRandomColorForNewSegment(
        wheelData,
        solidColors.filter((c) => !excludedColors.includes(c))
      );
      const colorInner = lightenColor(color, 20); // Lighten the color by 20%

      return { color, colorInner };
    }
  };

  const getMaxPosition = (wheelData = []) => {
    const sortedChart = wheelData?.sort((a, b) => a.position - b.position);
    return sortedChart[sortedChart.length - 1]?.position ?? 0;
  };

  const handleRotationData = (wheelData = [], direction = "prev") => {
    let chartdata = [...wheelData].map((item) => ({ ...item }));
    if (direction === "prev") {
      const firstElement = chartdata.shift();
      if (firstElement) {
        chartdata.push(firstElement);
      }
    } else if (direction === "next") {
      const lastElement = chartdata.pop();
      if (lastElement) {
        chartdata.unshift(lastElement);
      }
    }
    chartdata = chartdata.map((d, index) => ({ ...d, position: index * 10 }));
    return chartdata;
  };

  // ISP-9999: body extracted verbatim to hooks/logic/generateWheelPayload.js so
  // the save-payload builder (cell builder:14.11) is unit-assertable in isolation.
  // It is a pure function of its args — it reads no hook state — so delegation is
  // behaviour-preserving.
  const generatePayload = generateWheelPayload;
  return {
    segmentSettings: selectedArc?.settings || {},
    handleChange,
    getMaxPosition,
    handleRotationData,
    generatePayload,
    getColor,
  };
}
